import { useState } from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { getBenchmarkData } from '@api/benchmark';
import { intervalSelector, filterSelector } from '@store/overviewDashboard/base/selectors';
import { BENCHMARK_TYPES } from '../constants';

interface BenchmarkMetricsParams {
  dimension: string;
  metrics: string[];
  benchmarkType: string;
  percentile: number;
}

interface BenchmarkMetricsResponse {
  data: any[];
  adjustedInterval?: string;
  error: string | null;
}

const BEST_BENCHMARK_METRIC_MAP: Record<string, string> = {
  StreamingPerformanceIndex: 'StreamingPerformanceIndexBest',
};

const getBenchmarkMetricsForType = (metrics: string[], benchmarkType: string): string[] => {
  if (benchmarkType !== BENCHMARK_TYPES.BEST) {
    return metrics;
  }

  return metrics.map((metric) => BEST_BENCHMARK_METRIC_MAP[metric] || metric);
};

const getBenchmarkPercentileKeys = (percentile: number): string[] => {
  return [`${percentile}.0`, String(percentile), percentile.toFixed(1)];
};

const extractBenchmarkResults = (response: any, percentile: number): any[] => {
  const rawResult = get(response, ['data', 'benchmarkData', 'result'], []);

  if (Array.isArray(rawResult)) {
    return rawResult;
  }

  if (rawResult && typeof rawResult === 'object') {
    const percentileKeys = getBenchmarkPercentileKeys(percentile);
    for (const key of percentileKeys) {
      const bucket = rawResult[key];
      if (Array.isArray(bucket)) {
        return bucket;
      }
    }

    const firstBucket = Object.values(rawResult).find(Array.isArray);
    if (Array.isArray(firstBucket)) {
      return firstBucket;
    }
  }

  return [];
};

export const useBenchmarkMetrics = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const interval = useSelector(intervalSelector);
  const appliedFilter = useSelector(filterSelector);

  const fetchBenchmarkMetrics = async ({
    dimension,
    metrics,
    benchmarkType,
    percentile,
  }: BenchmarkMetricsParams): Promise<BenchmarkMetricsResponse> => {
    setLoading(true);
    setError(null);

    try {
      const filterRule = get(appliedFilter, 'rule', []);
      const benchmarkMetrics = getBenchmarkMetricsForType(metrics, benchmarkType);

      const query = {
        interval: `${interval.from}/${interval.to}`,
        filter: filterRule,
        groupBy: [dimension],
        metrics: benchmarkMetrics,
        options: {
          withBenchmarks: String(percentile),
        },
      };

      const response = await getBenchmarkData({ query });

      // Try different response paths for benchmark data
      let results = extractBenchmarkResults(response, percentile);
      if (!results || results.length === 0) {
        results = get(response, ['data', 0, 'data', 0, 'results'], []);
      }
      if (!results || results.length === 0) {
        results = get(response, ['data', 'result'], []);
      }

      // Extract adjustedInterval from the response (benchmark month detail)
      const adjustedInterval = get(response, ['data', 'benchmarkData', 'adjustedInterval'], null);

      setLoading(false);
      return { data: results, adjustedInterval, error: null };
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || err?.message || 'Failed to fetch benchmark metrics';
      setError(errorMessage);
      setLoading(false);
      return { data: [], error: errorMessage };
    }
  };

  return {
    fetchBenchmarkMetrics,
    loading,
    error,
  };
};
